import { z } from 'zod';
/**
 * Skill — a reusable, versioned markdown playbook ("how to do X") bound to a set
 * of agents. Shares the memory identity + binding + isolation model (DEV-16805),
 * but a distinct injection mechanism: skills materialize as `skills/<name>/SKILL.md`
 * into a Mastra Workspace and are description-triggered (loop-capable agents) or
 * inlined at build time (one-shot workflow agents).
 */
/** Also enforced at the DB + service layer. */
export const skillDescriptionMax = 500;
export const skillBodyMax = 20_000;
/** kebab-case, unique per org. */
export const SkillNameSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Skill name must be kebab-case');
// ─── v2 Knowledge Workspaces extensions ───────────────────────────────────────
// The design (v2 proposal §5.1-5.2) generalizes the existing agent-side Skill
// primitive to cover file-transform skills too. All additions are non-breaking:
// existing rows default to kind=AGENT and the doc-side fields are optional.
/**
 * What the skill does. Determines execution engine + trigger semantics.
 *
 * - `agent` — existing behavior: markdown instructions injected into a Mastra
 *   agent's Workspace. Bound via SkillBinding to specific agents.
 * - `conversion` — deterministic file→markdown converter (pdf-to-markdown,
 *   docx-to-markdown, vlm-caption, asr-transcribe). Content-addressed
 *   cacheable. Default trigger: on-commit.
 * - `passthrough` — identity: markdown/text/csv pass through unchanged. Keeps
 *   every file in an explicit skill chain so lineage is uniform.
 * - `custom-transform` — org-authored LLM recipe. Default trigger: manual so
 *   attach doesn't blow up costs.
 */
export const SkillKindSchema = z.enum(['agent', 'conversion', 'passthrough', 'custom-transform']);
/**
 * When a doc-side skill fires. Two modes only — the commit is the single
 * authoritative moment work runs:
 *
 * - `on-commit` — runs when the user commits a content-version draft. The
 *   default for conversion + passthrough. (Superseded the old `auto-on-upload`,
 *   which was misleadingly named: it fired at commit, never at upload.)
 * - `manual` — fires only on an explicit Run action. Default for
 *   custom-transform so attaching an LLM recipe doesn't blow up cost.
 *
 * Not applicable to `kind=agent`; agents are invoked by the runtime, not
 * triggered by file events.
 */
export const SkillTriggerModeSchema = z.enum(['on-commit', 'manual']);
/**
 * How the skill body is interpreted at runtime.
 *
 * - `claude-prompt` — body is a system prompt, executed against Claude with
 *   inputs as user message. Default for `kind=agent` and `kind=custom-transform`.
 * - `python-script` — body is (or references) a Python script executed in a
 *   Daytona sandbox. Default for most `kind=conversion` skills.
 * - `mcp-tool` — body references an MCP tool ID; runtime calls the tool.
 * - `native-converter` — deterministic bytewise transform in-process
 *   (currently only used by passthrough).
 */
export const SkillEngineKindSchema = z.enum([
    'claude-prompt',
    'python-script',
    'mcp-tool',
    'native-converter',
]);
// ─── SkillVersion ─────────────────────────────────────────────────────────────
export const SkillVersionSchema = z.object({
    id: z.string(),
    skillId: z.string(),
    /** Same-org invariant rides a composite FK to skill(id, orgId) — DEV-16808. */
    orgId: z.string(),
    versionNumber: z.number().int().positive(),
    /** Single-line trigger — Mastra's SkillsProcessor matches a task against this. */
    description: z.string().max(skillDescriptionMax),
    /** Markdown body materialized into `skills/<name>/SKILL.md`. */
    body: z.string().max(skillBodyMax),
    /** Required on every content edit (audit of why the version was cut). */
    comment: z.string(),
    createdBySubject: z.string().nullable(),
    createdAt: z.date(),
});
// ─── Skill (entity) ───────────────────────────────────────────────────────────
export const SkillSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: SkillNameSchema,
    /** Explicit, opt-in org-wide broadcast — reaches every agent in the org. */
    appliesToAll: z.boolean(),
    /** Pointer to the live version; null only transiently before the first version. */
    currentVersionId: z.string().nullable(),
    createdBySubject: z.string().nullable(),
    /** Skills soft-delete (unlike memories, which only archive). */
    deletedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    // ── v2 additions (populated by DB defaults; back-compat via migration) ──
    /** What the skill does. Existing rows migrate to 'agent'. */
    kind: SkillKindSchema,
    /** Glob patterns / MIME types this skill applies to (doc-side only). */
    mimeMatches: z.array(z.string()),
    /** Default trigger. Null for agent skills; overridable per SkillAttachment. */
    triggerMode: SkillTriggerModeSchema.nullable(),
    /** Execution engine. Existing rows migrate to 'claude-prompt'. */
    engineKind: SkillEngineKindSchema,
    /**
     * Optional Zod schema (serialized as JSON) declaring the shape of expected
     * outputs. Used by SkillRun to validate skill output before persisting
     * artifacts. Null = trust the skill body (legacy behavior).
     */
    ioSpec: z.unknown().nullable(),
    /** Skills shipped by Formant vs. authored by an org. */
    isBuiltIn: z.boolean(),
});
// ─── Binding (skill ↔ agent, many-to-many) ───────────────────────────────────
export const SkillBindingSchema = z.object({
    skillId: z.string(),
    agentId: z.string(),
    orgId: z.string(),
});
/**
 * A workflow LLM-node applicability target (DEV-17161) — the stable
 * `(workflowId, nodeKey)` a skill can be scoped to, mirroring the memory side.
 * Node bindings hang off the stable `Skill` row (not a version), so they edit
 * exactly like agent/collection bindings — no copy-on-write concerns.
 */
export const SkillNodeTargetSchema = z.object({
    workflowId: z.string().min(1),
    nodeKey: z.string().min(1),
});
/** API read shape — a skill with its resolved current version + bound agents/collections/nodes. */
export const SkillWithBindingsSchema = SkillSchema.extend({
    agentIds: z.array(z.string()),
    /** Agent-collection ids this skill is bound to (DEV-16821). */
    collectionIds: z.array(z.string()),
    /** Workflow LLM-node targets this skill is scoped to (DEV-17161). */
    nodeBindings: z.array(SkillNodeTargetSchema),
    currentVersion: SkillVersionSchema.nullable(),
});
/**
 * Read shape of GET /v1/skills/resolved (DEV-17002) — the skill set an agent
 * resolves at run time. Carries the promoted version but NOT the binding id
 * sets (resolution has already flattened appliesToAll/agent/collection paths).
 */
export const ResolvedSkillSchema = SkillSchema.extend({
    /**
     * true = explicitly bound to the caller; false = org-wide broadcast.
     * Optional for wire back-compat; the resolver always sets it. Feeds the
     * inline-budget selection (DEV-16824) — bound skills win under pressure.
     */
    bound: z.boolean().optional(),
    currentVersion: SkillVersionSchema.nullable(),
});
// ─── Create / Update ─────────────────────────────────────────────────────────
/**
 * Creates the skill + its first version.
 *
 * Kind-aware validation:
 * - `kind = 'agent'` (default): existing behavior — must bind to at least one
 *   agent OR set `appliesToAll` (agent-workspace loading target).
 * - `kind ∈ { conversion, passthrough, custom-transform }`: doc-side skill —
 *   no agent binding needed; `mimeMatches` should be non-empty so the skill
 *   can be routed to files.
 */
export const CreateSkillInputSchema = z
    .object({
    name: SkillNameSchema,
    description: z.string().min(1).max(skillDescriptionMax),
    body: z.string().min(1).max(skillBodyMax),
    // Agent-side fields (default when kind=agent)
    appliesToAll: z.boolean().default(false),
    agentIds: z.array(z.string()).default([]),
    /** Bind to agent collections (DEV-16821) — applies to every member agent. Optional. */
    collectionIds: z.array(z.string()).optional(),
    /** Bind to specific workflow LLM-agent nodes (DEV-17161). Optional; agent-kind only. */
    nodeBindings: z.array(SkillNodeTargetSchema).optional(),
    // v2 doc-side fields (default kind=agent keeps back-compat)
    kind: SkillKindSchema.default('agent'),
    mimeMatches: z.array(z.string()).default([]),
    triggerMode: SkillTriggerModeSchema.nullable().default(null),
    engineKind: SkillEngineKindSchema.default('claude-prompt'),
    ioSpec: z.unknown().nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'agent') {
        // Agent skills bind to agents, collections (DEV-16821), and/or workflow
        // nodes (DEV-17161), or applyToAll.
        if (!v.appliesToAll &&
            v.agentIds.length === 0 &&
            (v.collectionIds?.length ?? 0) === 0 &&
            (v.nodeBindings?.length ?? 0) === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'A skill must bind to at least one agent, collection, or workflow node, or set appliesToAll',
                path: ['agentIds'],
            });
        }
    }
    else {
        // Doc-side skills: agent binding fields are nonsensical here — reject
        // to prevent silent misuse from the API surface.
        if (v.appliesToAll) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'appliesToAll only applies to kind=agent skills',
                path: ['appliesToAll'],
            });
        }
        if (v.agentIds.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'agentIds only applies to kind=agent skills',
                path: ['agentIds'],
            });
        }
        if ((v.collectionIds?.length ?? 0) > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'collectionIds only applies to kind=agent skills',
                path: ['collectionIds'],
            });
        }
        if ((v.nodeBindings?.length ?? 0) > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'nodeBindings only applies to kind=agent skills',
                path: ['nodeBindings'],
            });
        }
        // A doc skill with no mimeMatches can never be routed. Warn via error.
        if (v.mimeMatches.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Doc-side skills must declare at least one MIME type in mimeMatches',
                path: ['mimeMatches'],
            });
        }
    }
});
/** A content edit cuts a new version — `comment` is required. */
export const UpdateSkillVersionInputSchema = z.object({
    description: z.string().min(1).max(skillDescriptionMax).optional(),
    body: z.string().min(1).max(skillBodyMax).optional(),
    comment: z.string().min(1),
});
/** Binding edits (who the skill applies to) do NOT cut a new version. */
export const UpdateSkillBindingsInputSchema = z.object({
    appliesToAll: z.boolean().optional(),
    agentIds: z.array(z.string()).optional(),
    /** Rebind the skill's collections (DEV-16821); omit to leave unchanged. */
    collectionIds: z.array(z.string()).optional(),
    /** Rebind the skill's workflow LLM-node targets (DEV-17161); omit to leave unchanged. */
    nodeBindings: z.array(SkillNodeTargetSchema).optional(),
});
/**
 * Metadata edits for doc-side fields (mimeMatches / triggerMode / engineKind).
 * Does NOT cut a new version — body/description are unchanged.
 * Built-in skills reject this operation (isBuiltIn guard in service).
 */
export const UpdateSkillMetaInputSchema = z
    .object({
    mimeMatches: z.array(z.string()).optional(),
    triggerMode: SkillTriggerModeSchema.nullable().optional(),
    engineKind: SkillEngineKindSchema.optional(),
})
    .refine((v) => v.mimeMatches !== undefined || v.triggerMode !== undefined || v.engineKind !== undefined, {
    message: 'At least one of mimeMatches, triggerMode, or engineKind must be provided',
});
/**
 * Version rollback (DEV-16963): re-point `currentVersionId` at an EXISTING
 * older version. Unlike a content edit this cuts NO new version — it promotes
 * an immutable historical one back to live. Idempotent (promoting the current
 * version is a no-op); 404 if the version doesn't belong to the skill.
 */
export const PromoteSkillVersionInputSchema = z.object({
    versionId: z.string().min(1),
});
//# sourceMappingURL=skill.js.map